import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  LoginRequest,
  LoginChallengeResponse,
  LoginVerificationChannel,
} from '../types';
import { authService } from '../services/auth.service';
import { policiesService } from '../services/policies.service';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasAcceptedPolicies: boolean;
  checkPolicies: () => Promise<boolean>;
  login: (
    empNo: string,
    password: string,
    channel?: LoginVerificationChannel,
  ) => Promise<LoginChallengeResponse>;
  completeLogin: (verificationId: string, code: string) => Promise<void>;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function isPublicAuthRoute(): boolean {
  const path = window.location.pathname;
  return path === '/login' || path === '/signup' || path === '/reset-password';
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() =>
    isPublicAuthRoute() ? null : authService.getUser(),
  );
  /** False immediately when a cached user exists — dashboard can paint before /auth/profile returns */
  const [isLoading, setIsLoading] = useState(() =>
    isPublicAuthRoute() ? false : !authService.getUser(),
  );
  const [hasAcceptedPolicies, setHasAcceptedPolicies] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (isPublicAuthRoute()) {
        authService.clearUser();
        setUser(null);
        setIsLoading(false);
        return;
      }

      try {
        const [profile, policyStatus] = await Promise.all([
          authService.getProfile(),
          policiesService.getUserStatus().catch(() => ({
            hasAcceptedAll: true,
            unacceptedPolicies: [] as { id: string }[],
          })),
        ]);
        authService.setUser(profile);
        setUser(profile);
        setHasAcceptedPolicies(policyStatus.hasAcceptedAll);
      } catch {
        authService.clearUser();
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPoliciesStatus = async () => {
    try {
      const status = await policiesService.getUserStatus();
      setHasAcceptedPolicies(status.hasAcceptedAll);
    } catch {
      setHasAcceptedPolicies(true);
    }
  };

  const checkPolicies = async (): Promise<boolean> => {
    try {
      const status = await policiesService.getUserStatus();
      setHasAcceptedPolicies(status.hasAcceptedAll);
      return status.hasAcceptedAll;
    } catch {
      return true;
    }
  };

  const login = async (
    empNo: string,
    password: string,
    channel?: LoginVerificationChannel,
  ): Promise<LoginChallengeResponse> => {
    const credentials: LoginRequest = { empNo, password, channel };
    return authService.login(credentials);
  };

  const completeLogin = async (verificationId: string, code: string) => {
    // verifyLogin returns { user } — the JWT is set as an HttpOnly cookie by the backend.
    const { user: loggedInUser } = await authService.verifyLogin(verificationId, code);
    authService.setUser(loggedInUser);
    setUser(loggedInUser);
    await checkPoliciesStatus();
  };

  const logout = () => {
    setUser(null);
    // Fire-and-forget: clears the HttpOnly cookie on the server, then redirects.
    void authService.logout();
  };

  const updateUser = (updatedUser: Partial<User>) => {
    if (user) {
      const mergedUser = { ...user, ...updatedUser };
      setUser(mergedUser);
      authService.setUser(mergedUser);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        hasAcceptedPolicies,
        checkPolicies,
        login,
        completeLogin,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
